import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { FaFilePdf, FaTimes } from "react-icons/fa";
import { ROI_TASKS } from "../assumptions";
import { formatMoney, formatNumber, formatPercent } from "../selectors";
import { ForecastChart, ForecastTable, TaskMinutesChart, ValueVsCostChart } from "./RoiCharts";

const CHART_WIDTH = 880;

function Slide({ eyebrow, title, children, footer }) {
  return (
    <section className="pl-slide">
      {eyebrow ? <div className="pl-slide-eyebrow">{eyebrow}</div> : null}
      {title ? <h2 className="pl-slide-title">{title}</h2> : null}
      <div className="pl-slide-body">{children}</div>
      <footer className="pl-slide-footer">{footer || "Addy AI"}</footer>
    </section>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="pl-slide-stat">
      <span className="pl-slide-stat-label">{label}</span>
      <span className="pl-slide-stat-value">{value}</span>
      {hint ? <span className="pl-slide-stat-hint">{hint}</span> : null}
    </div>
  );
}

/**
 * The ROI as a short client-facing deck: shown full screen, and saved as a
 * landscape PDF through the browser's print dialog. Always light, whatever
 * theme the dashboard is in, because it is meant to leave the building.
 */
export function RoiPresentation({ clientName, preparedBy, clientTypeLabel, inputs, result, forecast, onClose }) {
  useEffect(() => {
    document.body.classList.add("pl-presenting");
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("pl-presenting");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const client = clientName.trim() || "your team";
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const lastYear = forecast[forecast.length - 1];
  const footer = `Addy AI · ROI estimate for ${client} · ${today}`;
  const pricing =
    inputs.pricingModel === "perSeat"
      ? `${formatMoney(Number(inputs.pricePerSeat) || 0)} per seat per month × ${formatNumber(
          Number(inputs.seats) || 0,
        )} seats`
      : `${formatMoney(Number(inputs.pricePerLoan) || 0)} per funded loan`;

  return ReactDOM.createPortal(
    <div className="pl-deck-root" role="dialog" aria-modal="true" aria-label="Client ROI presentation">
      <div className="pl-deck-toolbar">
        <span>Client presentation · {client}</span>
        <div>
          <button type="button" className="pl-btn pl-btn--solid" onClick={() => window.print()}>
            <FaFilePdf aria-hidden="true" /> Save as PDF
          </button>
          <button type="button" className="pl-btn" onClick={onClose}>
            <FaTimes aria-hidden="true" /> Close
          </button>
        </div>
      </div>

      <div className="pl-deck">
        <Slide footer={footer}>
          <div className="pl-slide-cover">
            <div className="pl-slide-eyebrow">Addy AI · Return on investment</div>
            <h1>
              {formatNumber(result.monthlyHoursSaved)} hours a month back for {client}
            </h1>
            <p>
              {clientTypeLabel} · {formatNumber(Number(inputs.loansPerMonth) || 0)} loans a month ·{" "}
              {formatPercent(result.timeReduction)} less hands-on time per loan
            </p>
            <p className="pl-slide-muted">
              Prepared {today}
              {preparedBy.trim() ? ` by ${preparedBy.trim()}` : ""}
            </p>
          </div>
        </Slide>

        <Slide
          eyebrow="Where the time goes"
          title={`${formatNumber(result.minutesTodayPerLoan, 1)} minutes of hands-on work per loan becomes ${formatNumber(
            result.minutesWithAddyPerLoan,
            1,
          )}`}
          footer={footer}
        >
          <TaskMinutesChart result={result} width={CHART_WIDTH} height={280} />
          <div className="pl-slide-stats">
            <Stat label="Saved per loan" value={`${formatNumber(result.minutesSavedPerLoan, 1)} min`} />
            <Stat label="Less hands-on time" value={formatPercent(result.timeReduction)} />
            <Stat
              label="Turn time"
              value={
                result.turnTimeDaysSaved
                  ? `${formatNumber(result.turnTimeDaysSaved, 1)} days faster`
                  : "Not measured"
              }
            />
          </div>
        </Slide>

        <Slide eyebrow="What that is worth" title="Staff time saved against what Addy costs" footer={footer}>
          <div className="pl-slide-split">
            <div className="pl-slide-hero">
              <span className="pl-slide-stat-label">Return on Addy</span>
              <span className="pl-slide-hero-value">
                {result.roiMultiple === null ? "—" : `${formatNumber(result.roiMultiple, 2)}×`}
              </span>
              <span className="pl-slide-stat-hint">Staff time saved ÷ Addy cost</span>
            </div>
            <ValueVsCostChart result={result} width={600} height={170} />
          </div>
          <div className="pl-slide-stats">
            <Stat
              label="Staff hours back"
              value={`${formatNumber(result.monthlyHoursSaved)} a month`}
              hint={`${formatNumber(result.fteFreed, 1)} full-time people`}
            />
            <Stat label="Staff time saved" value={`${formatMoney(result.annualLaborSavings)} a year`} />
            <Stat label="Addy" value={`${formatMoney(result.annualAddyCost)} a year`} />
          </div>
          <p className="pl-slide-question">
            Would you spend {formatMoney(result.monthlyAddyCost)} a month to give your team back{" "}
            {formatNumber(result.monthlyHoursSaved)} hours?
          </p>
        </Slide>

        <Slide eyebrow="Three-year outlook" title={`${formatMoney(lastYear.net)} net after ${lastYear.year} years`} footer={footer}>
          <ForecastChart forecast={forecast} width={CHART_WIDTH} height={260} />
          <ForecastTable forecast={forecast} />
        </Slide>

        <Slide eyebrow="The assumptions" title="What these numbers are built on" footer={footer}>
          <div className="pl-slide-split pl-slide-split--top">
            <dl className="pl-slide-assumptions">
              <div>
                <dt>Client type</dt>
                <dd>{clientTypeLabel}</dd>
              </div>
              <div>
                <dt>Loans closed per month</dt>
                <dd>{formatNumber(Number(inputs.loansPerMonth) || 0)}</dd>
              </div>
              <div>
                <dt>Staff cost per hour</dt>
                <dd>{formatMoney(Number(inputs.hourlyCost) || 0)}</dd>
              </div>
              <div>
                <dt>Addy pricing</dt>
                <dd>{pricing}</dd>
              </div>
            </dl>
            <table className="pl-table pl-slide-table">
              <thead>
                <tr>
                  <th className="pl-left">Hands-on minutes per loan</th>
                  <th>Today</th>
                  <th>With Addy</th>
                </tr>
              </thead>
              <tbody>
                {result.tasks
                  .filter((task) => task.minutesToday > 0)
                  .map((task) => (
                    <tr key={task.key}>
                      <td className="pl-left">
                        {ROI_TASKS.find((entry) => entry.key === task.key)?.label}
                      </td>
                      <td>{formatNumber(task.minutesToday, 1)}</td>
                      <td>{formatNumber(task.minutesWithAddy, 1)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className="pl-slide-muted">
            Calculated from the numbers above, shared by {client}, and Addy's pricing. Only
            closed loans are counted, so files that fall out add savings this does not show.
          </p>
        </Slide>
      </div>
    </div>,
    document.body,
  );
}
